import React from "react";
import {
    Badge, Box, Button, Flex, Icon, Modal, ModalBody, ModalCloseButton, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Text, Wrap, WrapItem, useColorModeValue, useDisclosure,
} from "@chakra-ui/react";
import {
    MdAllInclusive, MdHourglassEmpty, MdCheckCircle, MdCancel,
    MdPerson, MdGroup, MdBusiness, MdChevronLeft, MdChevronRight, MdTune, MdClose,
} from "react-icons/md";

const STATUS_OPTIONS = [
    { value: "all",      label: "Tous",       icon: MdAllInclusive,   color: "#9e9e9e" },
    { value: "pending",  label: "En attente", icon: MdHourglassEmpty, color: "#ff9800" },
    { value: "accepted", label: "Acceptés",   icon: MdCheckCircle,    color: "#4caf50" },
    { value: "declined", label: "Refusés",    icon: MdCancel,         color: "#f44336" }
];

const ENTITY_OPTIONS = [
    { value: "all",    label: "Tous",           icon: MdAllInclusive, color: "#9e9e9e" },
    { value: "user",   label: "Artistes",       icon: MdPerson,       color: "#2196f3" },
    { value: "group",  label: "Groupes",        icon: MdGroup,        color: "#9c27b0" },
    { value: "entity", label: "Établissements", icon: MdBusiness,     color: "#ff9800" }
];

/**
 * Renders a single filter toggle button.
 * @param {Object} opt - Option descriptor ({value, label, icon, color})
 * @param {boolean} isSelected - Whether this option is active
 * @param {Function} onChange - Callback fired with the option value
 * @returns {React.ReactElement}
 */
function renderFilterButton(opt, isSelected, onChange) {
    return (
        <WrapItem key={opt.value}>
            <Button size="sm" borderRadius="full" onClick={() => onChange(opt.value)}
                colorScheme={isSelected ? "brand" : "gray"} variant={isSelected ? "solid" : "outline"}
                leftIcon={<Icon as={opt.icon} color={isSelected ? "currentColor" : opt.color} />}>
                {opt.label}
            </Button>
        </WrapItem>
    );
}

/**
 * Renders a labeled, wrapping row of filter buttons for a single filter group.
 * @param {Object} props
 * @param {string} props.label - Leading group label (e.g. "Statut")
 * @param {Array} props.options - Filter options
 * @param {string} props.value - Currently selected value
 * @param {Function} props.onChange - Callback fired with the new value
 * @returns {React.ReactElement}
 */
function FilterRow({ label, options, value, onChange }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box mb="16px">
            <Text fontSize="xs" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase" color={labelColor} mb="8px">
                {label}
            </Text>
            <Wrap spacing="6px">
                {options.map((opt) => renderFilterButton(opt, value === opt.value, onChange))}
            </Wrap>
        </Box>
    );
}

/**
 * The "Filtres" button shown to the left of the search bar, with a count badge
 * of active (non-"all") filters. Opens the central filter modal.
 * @param {Object} props
 * @param {number} props.activeCount - Number of active filters
 * @param {Function} props.onOpen - Opens the filter modal
 * @returns {React.ReactElement}
 */
function FiltersButton({ activeCount, onOpen }) {
    return (
        <Button flexShrink="0" variant={activeCount ? "solid" : "outline"} colorScheme={activeCount ? "brand" : "gray"}
            leftIcon={<Icon as={MdTune} />} onClick={onOpen} borderRadius="12px">
            Filtres
            {activeCount > 0 && <Badge ml="6px" colorScheme="brand" borderRadius="full" px="6px">{activeCount}</Badge>}
        </Button>
    );
}

/**
 * Centered modal holding the status and entity-type filter groups. Selecting an
 * option applies it immediately; a "Effacer" shortcut resets all filters.
 * @param {Object} props - modal state, filter values, and change/clear handlers
 * @returns {React.ReactElement}
 */
function FiltersModal({ isOpen, onClose, status, entityType, onStatusChange, onEntityTypeChange, onClear, activeCount }) {
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Filtres</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <FilterRow label="Statut" options={STATUS_OPTIONS} value={status} onChange={onStatusChange} />
                    <Box mb="0"><FilterRow label="Type" options={ENTITY_OPTIONS} value={entityType} onChange={onEntityTypeChange} /></Box>
                </ModalBody>
                <ModalFooter>
                    {activeCount > 0 && (
                        <Button variant="ghost" colorScheme="gray" mr="auto" leftIcon={<Icon as={MdClose} />} onClick={onClear}>
                            Effacer
                        </Button>
                    )}
                    <Button colorScheme="brand" onClick={onClose}>Fermer</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/**
 * Filter control: a "Filtres" button (placed left of the search bar) that opens
 * a centered modal with the status and entity-type filters.
 * @param {Object} props
 * @param {string} props.status - Selected status value
 * @param {string} props.entityType - Selected entity type value
 * @param {Function} props.onStatusChange - Status change handler
 * @param {Function} props.onEntityTypeChange - Entity type change handler
 * @param {Function} props.onClear - Reset all filters handler
 * @returns {React.ReactElement}
 */
function CertificationFilters({ status, entityType, onStatusChange, onEntityTypeChange, onClear }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const activeCount = (status !== "all" ? 1 : 0) + (entityType !== "all" ? 1 : 0);
    return (
        <>
            <FiltersButton activeCount={activeCount} onOpen={onOpen} />
            <FiltersModal isOpen={isOpen} onClose={onClose} status={status} entityType={entityType}
                onStatusChange={onStatusChange} onEntityTypeChange={onEntityTypeChange}
                onClear={onClear} activeCount={activeCount} />
        </>
    );
}

/**
 * Renders the list pagination control; hidden when there is at most one page.
 * @param {Object} props
 * @param {number} props.page - Current page (1-indexed)
 * @param {number} props.pages - Total number of pages
 * @param {Function} props.onPageChange - Callback fired with the new page number
 * @returns {React.ReactElement|null}
 */
function ListPagination({ page, pages, onPageChange }) {
    if (!pages || pages <= 1) return null;
    return (
        <Flex align="center" justify="center" gap="12px" mt="12px">
            <Button size="sm" variant="outline" colorScheme="brand" isDisabled={page <= 1}
                leftIcon={<Icon as={MdChevronLeft} />} onClick={() => onPageChange(Math.max(1, page - 1))}>
                Précédent
            </Button>
            <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "secondaryGray.400" }}>
                Page {page} / {pages}
            </Text>
            <Button size="sm" variant="outline" colorScheme="brand" isDisabled={page >= pages}
                rightIcon={<Icon as={MdChevronRight} />} onClick={() => onPageChange(page + 1)}>
                Suivant
            </Button>
        </Flex>
    );
}

export { CertificationFilters, ListPagination };
